import { useRecoilState } from "recoil";
import { useEndpointProvider } from "../Providers/EndpointProvider";
import { useBackendLinksProvider } from "../Providers/BackendLinksProvider";
import { useWorkspacesProvider } from "../Providers/WorkspacesProvider";
import { annotationShortcutsAtom, loadHotkeysFromConfigs } from "../Pages/Data/Visualize/DataReview/Atoms/Hotkeys";

/**
 * Hook for managing annotation keyboard shortcuts
 */
export const useAnnotationShortcutService = () => {
	const endpointProvider = useEndpointProvider();
	const { LINKS } = useBackendLinksProvider();
	const workspacesProvider = useWorkspacesProvider();
	const [annotationShortcuts, setAnnotationShortcuts] = useRecoilState(annotationShortcutsAtom);

	/**
	 * Builds the payload for annotation shortcut API calls
	 */
	const buildAnnotationPayload = (data: any) => ({
		trigger: (data?.triggers && data.triggers[0]) ? data.triggers[0] : null,
		text: data?.text ?? data?.data?.text ?? "",
		description: data?.description ?? data?.data?.description ?? "",
		tags: data?.tags ?? data?.data?.tags ?? [],
		type: data?.type ?? data?.data?.type ?? "",
		color: data?.color ?? data?.data?.color ?? "#207DEA",
		opacity: data?.opacity ?? data?.data?.opacity ?? 1,
		modalities: data?.modalities ?? data?.data?.modalities ?? []
	});

	/**
	 * Fetches all annotation shortcuts from the backend
	 */
	async function getAnnotationShortcuts() {
		try {
			return await endpointProvider.post(LINKS.ACCOUNT.GET_ANNOTATION_SHORTCUTS, {
				workspace_name: workspacesProvider.selectedWorkspace
			});
		} catch (error) {
			console.error(error);
			return [];
		}
	}

	/**
	 * Creates a new annotation shortcut
	 * After creation, refetches all annotation shortcuts and updates the atom
	 * @param data - The annotation shortcut data
	 */
	async function createAnnotationShortcut(data: any): Promise<void> {
		const payload = buildAnnotationPayload(data);

		await endpointProvider.post(LINKS.ACCOUNT.CREATE_ANNOTATION_SHORTCUT, payload);
		const shortcuts = await getAnnotationShortcuts();
		setAnnotationShortcuts(loadHotkeysFromConfigs(shortcuts || []));
	}

	/**
	 * Updates an existing annotation shortcut
	 * After update, refetches all annotation shortcuts and updates the atom
	 * @param id - The ID of the annotation shortcut to update
	 * @param data - The updated annotation shortcut data
	 */
	async function updateAnnotationShortcut(id: string, data: any): Promise<void> {
		const payload = buildAnnotationPayload(data);

		await endpointProvider.post(LINKS.ACCOUNT.UPDATE_ANNOTATION_SHORTCUT, {
			id,
			...payload
		});
		const shortcuts = await getAnnotationShortcuts();
		setAnnotationShortcuts(loadHotkeysFromConfigs(shortcuts || []));
	}

	/**
	 * Deletes annotation shortcuts by IDs
	 * After deletion, refetches all annotation shortcuts and updates the atom
	 * @param ids - Array of annotation shortcut IDs to delete
	 */
	async function deleteAnnotationShortcuts(ids: string[]): Promise<void> {
		await endpointProvider.post(LINKS.ACCOUNT.DELETE_ANNOTATION_SHORTCUTS, {
			workspace_name: workspacesProvider.selectedWorkspace,
			ids
		});
		const shortcuts = await getAnnotationShortcuts();
		setAnnotationShortcuts(loadHotkeysFromConfigs(shortcuts || []));
	}

	/**
	 * Resets annotation shortcuts to default
	 * Deletes all existing annotation shortcuts and creates the default one
	 */
	async function resetAnnotationShortcuts(): Promise<void> {
		await endpointProvider.post(LINKS.ACCOUNT.RESET_ANNOTATION_SHORTCUTS, {
			workspace_name: workspacesProvider.selectedWorkspace
		});
		const shortcuts = await getAnnotationShortcuts();
		setAnnotationShortcuts(loadHotkeysFromConfigs(shortcuts || []));
	}

	return {
		createAnnotationShortcut,
		updateAnnotationShortcut,
		deleteAnnotationShortcuts,
		resetAnnotationShortcuts,
		getAnnotationShortcuts,
		annotationShortcuts,
	};
};

